import { inputVariants } from '../Input/index';
import { cn, cva } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import React, { forwardRef } from 'react';

export const InputWithAdornmentsVariants = cva('', {
    variants: {
        variant: {
            default: '',
            light: '',
            errorDefault: '',
            errorLight: '',
        },
        disabled: {
            false: '',
            true: 'cursor-not-allowed',
        },
        preAdornment: {
            false: '',
            true: 'pl-0',
        },
        postAdornment: {
            false: '',
            true: 'pr-0',
        },
    },
    compoundVariants: [
        {
            disabled: true,
            variant: 'default',
            className: 'opacity-50',
        },
        {
            disabled: true,
            variant: 'light',
            className: 'bg-gray-500',
        },
    ],
    defaultVariants: {
        variant: 'default',
        disabled: false,
        preAdornment: false,
        postAdornment: false,
    },
});

export interface AdornedInputWrapperProps
    extends
        React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof inputVariants>,
        VariantProps<typeof InputWithAdornmentsVariants> {
    disabled?: boolean;
}

const Wrapper = ({
    children,
    variant,
    dimensions,
    className,
    disabled,
    preAdornment,
    postAdornment,
}: AdornedInputWrapperProps) => {
    return (
        <div
            className={cn(
                inputVariants({ variant, dimensions, className }),
                InputWithAdornmentsVariants({ variant, disabled, preAdornment, postAdornment, className }),
                className,
            )}
        >
            {children}
        </div>
    );
};

interface AdornmentProps extends React.HTMLAttributes<HTMLSpanElement> {}

const Adornment = ({ children, className, ...props }: AdornmentProps) => {
    return (
        <span className={cn('flex items-center px-1.5', className)} {...props}>
            {children}
        </span>
    );
};

export interface AdornedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    preAdornment?: React.ReactNode;
    postAdornment?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, AdornedInputProps>(
    ({ preAdornment, postAdornment, className, disabled, ...props }, ref) => {
        return (
            <input
                ref={ref}
                {...props}
                disabled={disabled}
                className={cn(
                    'w-full bg-transparent outline-hidden focus-visible:outline-hidden',
                    {
                        'pl-1.5': !!preAdornment,
                        'pr-1.5': !!postAdornment,
                    },
                    disabled && 'cursor-not-allowed opacity-50',
                    className,
                )}
            />
        );
    },
);
Input.displayName = 'Input';

interface ComposedAdornedInputProps
    extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputVariants> {
    preAdornment?: React.ReactNode;
    postAdornment?: React.ReactNode;
}

const ComposedAdornedInput = forwardRef<HTMLInputElement, ComposedAdornedInputProps>(
    ({ className, variant, dimensions, preAdornment, postAdornment, disabled, ...props }, ref) => {
        return (
            <Wrapper
                className={className}
                variant={variant}
                dimensions={dimensions}
                disabled={disabled}
                preAdornment={Boolean(preAdornment)}
                postAdornment={Boolean(postAdornment)}
            >
                {preAdornment && <Adornment>{preAdornment}</Adornment>}
                <Input ref={ref} disabled={disabled} {...props} />
                {postAdornment && <Adornment>{postAdornment}</Adornment>}
            </Wrapper>
        );
    },
);
ComposedAdornedInput.displayName = 'ComposedAdornedInput';

export { ComposedAdornedInput, Input, Wrapper, Adornment };
